import { useEffect, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { LOGO_ACCEPT, isLogoFile, logoOf, useAttachments, useReportMutations, type AttachmentDto } from '../api/hooks'
import { problemOf } from '../api/client'
import { SecureImage } from './ui'

const MAX_LOGO_BYTES = 10 * 1024 * 1024

/** What the finish dialog decided about the logo: a new file to upload, or the current one to drop. */
export interface LogoChoice { file: File | null; removeCurrent: boolean }
export const NO_LOGO_CHANGE: LogoChoice = { file: null, removeCurrent: false }

function useLogoCheck() {
  const { t } = useTranslation()
  return (file: File): string | null => (!isLogoFile(file) ? t('logo.onlyImages') : file.size > MAX_LOGO_BYTES ? t('logo.tooLarge') : null)
}

/** Object URL of a picked file for the preview; released when the file changes or the component goes away. */
function useFilePreview(file: File | null) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) { setUrl(null); return }
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  return url
}

/**
 * "Şirket logosu (isteğe bağlı)" in the Boş Rapor finish dialog: pick a PNG/JPEG, preview it, remove it again.
 * Nothing is uploaded here — the dialog uploads the choice right before completing the report.
 */
export function LogoField({ current, value, onChange, disabled }: { current?: AttachmentDto; value: LogoChoice; onChange: (next: LogoChoice) => void; disabled?: boolean }) {
  const { t } = useTranslation()
  const check = useLogoCheck()
  const preview = useFilePreview(value.file)
  const [error, setError] = useState<string | null>(null)
  const showCurrent = !value.file && !!current && !value.removeCurrent
  const hasLogo = !!value.file || showCurrent

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // so the same file can be picked again
    if (!file) return
    const problem = check(file)
    setError(problem)
    if (!problem) onChange({ file, removeCurrent: false })
  }
  function remove() {
    setError(null)
    onChange({ file: null, removeCurrent: !!current })
  }

  return (
    <div className="logo-field">
      <span className="logo-field-label">{t('logo.optional')}</span>
      <span className="muted small">{t('logo.hint')}</span>
      <div className="logo-row">
        {preview ? <img src={preview} alt={t('logo.title')} className="logo-preview" /> : showCurrent && current ? <SecureImage fileId={current.fileId} alt={t('logo.title')} className="logo-preview" /> : <span className="logo-preview logo-empty muted small">{t('logo.none')}</span>}
        <div className="row wrap logo-buttons">
          <label className={`button secondary small-btn ${disabled ? 'disabled' : ''}`}>{hasLogo ? t('logo.change') : t('logo.pick')}<input type="file" accept={LOGO_ACCEPT} hidden disabled={disabled} onChange={onPick} /></label>
          {hasLogo ? <button type="button" className="ghost small-btn" onClick={remove} disabled={disabled}>{t('logo.remove')}</button> : null}
        </div>
      </div>
      {value.file ? <span className="muted small">{value.file.name}</span> : null}
      {error ? <span className="error small" role="alert">{error}</span> : null}
    </div>
  )
}

/**
 * "Logo" control of the report page (owner / admin): the current logo's thumbnail and "Logo yükle / değiştir".
 * Uploading replaces the previous logo and drops the cached export files on the server, so the next PowerPoint download carries it.
 */
export function ReportLogoControl({ reportId }: { reportId: string }) {
  const { t } = useTranslation()
  const check = useLogoCheck()
  const attachments = useAttachments(reportId)
  const { uploadLogo } = useReportMutations(reportId)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const current = logoOf(attachments.data)

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setDone(false)
    const problem = check(file)
    setError(problem)
    if (problem) return
    uploadLogo.mutate(file, { onSuccess: () => setDone(true), onError: (err) => setError(problemOf(err).title ?? t('logo.uploadFailed')) })
  }

  return (
    <div className="logo-control" role="group" aria-label={t('logo.title')}>
      <span className="download-label">{t('logo.title')}</span>
      {current ? <SecureImage fileId={current.fileId} alt={t('logo.title')} className="logo-thumb" /> : <span className="muted small">{t('logo.none')}</span>}
      <label className={`button secondary small-btn ${uploadLogo.isPending ? 'disabled' : ''}`} title={t('logo.hint')}>
        {uploadLogo.isPending ? t('logo.uploading') : t('logo.uploadOrChange')}
        <input type="file" accept={LOGO_ACCEPT} hidden disabled={uploadLogo.isPending} onChange={onPick} />
      </label>
      {done && !error ? <span className="muted small" role="status">{t('logo.updated')}</span> : null}
      {error ? <span className="error small" role="alert">{error}</span> : null}
    </div>
  )
}
